package com.solevo.dist.pages.orderdetails

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.solevo.dist.components.DashboardPageLayout
import com.solevo.dist.components.RequireAuth
import com.solevo.dist.components.WaitForAuthAndViewModel
import com.solevo.dist.util.Res
import com.solevo.dist.viewmodels.DeliveryNoteViewModel
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgb
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.solevo.dist.i18n.LocalStrings
import com.solevo.dist.theme.styles.tableHeaderCell
import com.solevo.dist.theme.styles.tableBodyCell
import com.solevo.dist.theme.styles.tableRow
import com.solevo.dist.theme.styles.tableHeaderRow
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronLeft
import com.varabyte.kobweb.silk.components.icons.fa.FaDownload
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import kotlinx.browser.window
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import com.solevo.dist.navigation.Screen
import kotlinx.serialization.Serializable

@Page("/orderdetails/deliverynote")
@Composable
fun DeliveryNoteScreen() {
    RequireAuth { DeliveryNotePage() }
}

@Composable
fun DeliveryNotePage() {
    val raw = remember { window.localStorage.getItem("delivery_note") ?: "" }
    
    WaitForAuthAndViewModel(
        key = raw,
        viewModelFactory = { DeliveryNoteViewModel(raw) },
        onViewModelReady = { vm -> vm.start() }
    ) { vm ->
        val ui by vm.state.collectAsState()

        DashboardPageLayout {
            Column(Modifier.fillMaxWidth().padding(24.px)) {
            // Title row with back + download button on the right
            val context = rememberPageContext()
            Row(Modifier.fillMaxWidth().alignItems(AlignItems.Center).justifyContent(JustifyContent.SpaceBetween)) {
                Row(Modifier.alignItems(AlignItems.Center)) {
                    FaChevronLeft(
                        modifier = Modifier
                            .margin(top = 4.px)
                            .cursor(Cursor.Pointer)
                            .onClick { context.router.navigateTo("/orders") },
                        size = IconSize.LG
                    )
                    SpanText(
                        "Delivery note ${ui.id}",
                        Modifier.margin(left = 12.px).fontSize(28.px).fontWeight(FontWeight.Bold)
                    )
                }

                if (!ui.docUrl.isNullOrBlank()) {
                    Row(
                        Modifier
                            .alignItems(AlignItems.Center)
                            .backgroundColor(rgb(24, 119, 242))
                            .color(rgb(255, 255, 255))
                            .padding(leftRight = 16.px, topBottom = 10.px)
                            .borderRadius(8.px)
                            .cursor(Cursor.Pointer)
                            .onClick { window.open(ui.docUrl!!, target = "_blank") }
                    ) {
                        FaDownload(modifier = Modifier.color(rgb(255, 255, 255)), size = IconSize.SM)
                        val strings = LocalStrings.current
                        SpanText(strings.download, Modifier.margin(left = 10.px).color(rgb(255, 255, 255)))
                    }
                }
            }

            Box(Modifier.height(16.px))

            // Details card (like Android)
            Column(
                Modifier
                    .fillMaxWidth()
                    .borderRadius(8.px)
            ) {
                Row(Modifier.fillMaxWidth().padding(16.px).alignItems(AlignItems.Center)) {
                    Image(
                        modifier = Modifier
                            .size(18.px)
                            .borderRadius(4.px)
                            .styleModifier { property("object-fit", "cover") },
                        src = Res.Icon.OrderIcons.calendar,
                        alt = "Info icon"
                    )
                    Column(
                        Modifier
                            .margin(left = 8.px)
                            .justifyContent(JustifyContent.Center)
                            .alignItems(AlignItems.Center)
                    ) {
                        SpanText(
                            "Pickup date",
                            Modifier
                                .color(rgb(120, 130, 140))
                                .styleModifier { property("text-align", "center") }
                        )
                        SpanText(
                            ui.pickingDateText.ifBlank { "—" },
                            Modifier
                                .margin(top = 4.px)
                                .styleModifier { property("text-align", "center") }
                        )
                    }
                }
            }

            Box(Modifier.height(24.px))

            // Items table header
            val strings = LocalStrings.current
            SpanText(strings.allItems, Modifier.fontWeight(FontWeight.SemiBold))
            Box(Modifier.height(10.px))

            Row(
                Modifier
                    .fillMaxWidth()
                    .display(org.jetbrains.compose.web.css.DisplayStyle.Grid)
                    .styleModifier { property("grid-template-columns", "1.2fr 0.8fr 1fr 0.6fr 0.8fr") }
                    .tableHeaderRow()
            ) {
                SpanText(strings.headerProduct, Modifier.tableHeaderCell())
                SpanText(strings.headerTotal, Modifier.tableHeaderCell())
                SpanText(strings.headerWarehouse, Modifier.tableHeaderCell())
                SpanText(strings.headerUnits, Modifier.tableHeaderCell())
                SpanText(strings.headerPricePerUnit, Modifier.tableHeaderCell())
            }

            val listContext = rememberPageContext()
            ui.pickedItems.forEach { item ->
                Row(
                    Modifier
                        .fillMaxWidth()
                        .display(org.jetbrains.compose.web.css.DisplayStyle.Grid)
                        .styleModifier { property("grid-template-columns", "1.2fr 0.8fr 1fr 0.6fr 0.8fr") }
                        .tableRow()
                        .cursor(Cursor.Pointer)
                        .onClick { listContext.router.navigateTo(Screen.ProductDetailsPage.withId(item.productId)) }
                ) {
                    SpanText(item.name.ifBlank { "—" }, Modifier.tableBodyCell())
                    SpanText(item.totalPrice?.toString() ?: "—", Modifier.tableBodyCell())
                    SpanText(item.warehouse ?: "-", Modifier.tableBodyCell())
                    SpanText(item.quantity.toString(), Modifier.tableBodyCell())
                    SpanText(item.pricePerUnit?.toString() ?: "—", Modifier.tableBodyCell())
                }
            }

            // download button is in header (right side)
            }
        }
    }
}



